/**
 * Utility functions for formatting and display
 */

import { FRACTION_DENOMINATORS } from './constants';

/**
 * Convert decimal to fraction for display
 * @param decimal - The decimal number to convert
 * @returns Formatted fraction string
 */
export const decimalToFraction = (decimal: number): string => {
  if (decimal === 0) return '0"';

  // Handle sign separately 
  const isNegative = decimal < 0;
  const absValue = Math.abs(decimal);
  
  // Get the whole number part
  const wholePart = Math.floor(absValue);
  
  // Get the decimal part
  const decimalPart = absValue - wholePart;
  
  // If decimal part is very small, treat as zero
  if (decimalPart < 0.001) {
    return `${isNegative ? '-' : ''}${wholePart}"`;
  }
  
  // Find closest fraction with denominator up to 16
  let closestFraction = { numerator: 0, denominator: 16 };
  let minDifference = 1;
  
  // For each possible denominator
  for (const denominator of FRACTION_DENOMINATORS) {
    // Calculate the numerator that would most closely match our decimal
    const numerator = Math.round(decimalPart * denominator);
    const difference = Math.abs(decimalPart - numerator / denominator);
    
    // If this is a better match, store it
    if (difference < minDifference) {
      minDifference = difference;
      closestFraction = { numerator, denominator };
      
      // If it's an exact match (or very close), break early
      if (difference < 0.0001) break;
    }
  }
  
  // Simplify the fraction
  const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
  const divisor = gcd(closestFraction.numerator, closestFraction.denominator);
  
  const simplifiedNumerator = closestFraction.numerator / divisor;
  const simplifiedDenominator = closestFraction.denominator / divisor;
  
  // Special case: if fraction equals 1, increment whole number and set fraction to 0
  if (simplifiedNumerator === simplifiedDenominator) {
    return `${isNegative ? '-' : ''}${wholePart + 1}"`;
  }
  
  // Return properly formatted string
  if (wholePart === 0) {
    return `${isNegative ? '-' : ''}${simplifiedNumerator}/${simplifiedDenominator}"`;
  } else if (simplifiedNumerator === 0) {
    return `${isNegative ? '-' : ''}${wholePart}"`;
  } else {
    return `${isNegative ? '-' : ''}${wholePart} ${simplifiedNumerator}/${simplifiedDenominator}"`;
  }
};

/**
 * Format measurement for display (inches or feet-inches)
 * @param value - The numeric value to format
 * @param useFootInch - Whether to use feet-inches format
 * @returns Formatted measurement string
 */
export const formatMeasurement = (value: number, useFootInch: boolean = false): string => {
  if (value === 0) return useFootInch ? "0\"" : '0"';
  
  const isNegative = value < 0;
  const absValue = Math.abs(value);
  
  if (useFootInch) {
    // Convert to feet and inches format (e.g., 3'-11 3/4")
    const feet = Math.floor(absValue / 12);
    const inches = absValue % 12;
    
    // If the absolute value is less than 12 inches (1 foot), just return inches
    if (feet === 0) {
      return decimalToFraction(value);
    }
    
    // If there are no inches, just return feet
    if (Math.abs(inches) < 0.001) {
      return `${isNegative ? '-' : ''}${feet}'`;
    }
    
    // Format the inches as a fraction
    const inchesFormatted = decimalToFraction(inches).replace('"', '');
    
    // Apply negative sign to the entire measurement if needed
    return `${isNegative ? '-' : ''}${feet}'-${inchesFormatted}"`;
  }
  
  // Default case: return inches with quotation mark
  return decimalToFraction(value);
};

/**
 * Convert fraction string to decimal for comparison
 * @param fractionStr - The fraction string to convert
 * @returns Decimal number
 */
export const fractionToDecimal = (fractionStr: string): number => {
  // Remove quotation mark if present
  const cleanStr = fractionStr.replace('"', '');
  
  if (cleanStr.includes(' ')) {
    // Mixed number (e.g., "10 3/8")
    const [whole, fraction] = cleanStr.split(' ');
    const [numerator, denominator] = fraction.split('/').map(Number);
    return parseInt(whole) + (numerator / denominator);
  } else if (cleanStr.includes('/')) {
    // Simple fraction (e.g., "3/8")
    const [numerator, denominator] = cleanStr.split('/').map(Number);
    return numerator / denominator;
  } else {
    // Whole number
    return parseFloat(cleanStr);
  }
};
